import React from 'react';
import { connect } from 'react-redux';
import Highcharts from 'highcharts';
import _ from 'lodash';
import moment from 'moment';

import dateUtils from '../../../util/dates';
const highchartUtils = require('../../../util/highcharts')(Highcharts);

const chartConfig = {
  exporting: {
    enabled: false
  },
  credits: {
    enabled: false
  },
  chart: {
    backgroundColor: '#fff',
    type: 'spline'
  },
  title: {
    text: ''
  },
  tooltip: {
    useHTML: true,
    shared: true,
    shadow: false,
    hideDelay: 0,
    borderRadius: 5,
    borderWidth: 0,
    formatter: function() {}
  },
  xAxis: {
    type: 'datetime',
    gridLineWidth: 1,
    showFirstLabel: true,
    showLastTickLabel: true,
    title: {
      text: null
    }
  },
  yAxis: {
    gridLineWidth: 1,
    labels: {
      enabled: true
    },
    title: {
      text: null
    },
    showFirstLabel: true,
    showLastTickLabel: true
  },
  legend: {
    enabled: false
  },
  plotOptions: {
    series: {
      stickyTracking: false
    },
    spline: {
      marker: {
        symbol: 'diamond',
        enabledThreshold: 2,
        lineWidth: 1,
        radius: 5
      },
      states: {
        hover: {
          marker: {
            enabled: false
          }
        }
      }
    }
  },
  drilldown: {
    allowPointDrilldown: false,
    series: []
  },
  series: [
    {
      data: []
    }
  ]
};

class LineChart extends React.Component {
  defaultProps = {
    unit: '',
    tooltipLabel: '',
    data: []
  }; 

  componentDidMount() {
    highchartUtils.setDefaults();
    const that = this;
    chartConfig.tooltip.formatter = function() {
      return that.tooltipFormatter(that, this);
    };

    this.chart = new Highcharts['Chart'](`${this.props.id}-details`, chartConfig);

    this.updateTimeLabelsFormat(this.props.filters.dateFormat);

    if (this.props.data) {
      this.updateChart();
    }
  }

  componentDidUpdate(prevProps) {
    if (
      !_.isEqual(prevProps.data, this.props.data) ||
      !_.isEqual(prevProps.detailed, this.props.detailed)
    ) {
      this.updateChart();
    }
    if (prevProps.filters.dateFormat !== this.props.filters.dateFormat) {
      this.updateTimeLabelsFormat(this.props.filters.dateFormat);
    }
    this.updateFontSize();    
  }

  componentWillUnmount() {
    this.chart.destroy();
  }

  updateChart() {
    while (this.chart.series.length > 0) {
      this.chart.series[0].remove(false);
    }

    if (!this.props.data.length) {
      return false;
    }

    const detailsEnabled = typeof this.props.detailed === 'boolean';
    this.chart.update({ legend: { enabled: detailsEnabled && this.props.detailed } });

    _.forEach([...this.props.data], (s, i) => {
      if (detailsEnabled && this.props.detailed && i > 0) {
        this.chart.addSeries(s, false);
      } else if (detailsEnabled && !this.props.detailed && i === 0) {
        this.chart.addSeries(s, false);
      } else if (!detailsEnabled) {
        this.chart.addSeries(s, false);
      }
    });

    this.chart.redraw();
  }

  updateFontSize = () => {
    const opt = {
      exporting: {
        enabled: this.props.isFullScreen
      },
      plotOptions: {
        spline: {
          dataLabels: {
            style: {
              fontSize: this.props.isFullScreen ? '16px' : '11px',
              fontWeight: 'bold',
              color: 'contrast'
            }
          }
        }
      }
    };

    if (this.props.isFullScreen) {
      opt.yAxis = {
        gridLineWidth: 1,
        labels: {
          enabled: true
        }
      };
    } else {
      opt.yAxis = {
        gridLineWidth: 0,
        labels: {
          enabled: false
        }
      };
    }

    this.chart.update(opt);
    this.chart.reflow();
  };

  updateTimeLabelsFormat(dateFormat) {
    let config = { xAxis: {} };
    config.xAxis.dateTimeLabelFormats = dateUtils.createHighchartFormats(dateFormat);

    this.chart.update(config);
  }

  tooltipFormatter(comp, ev) {
    if (comp.props.id === 'percentiles-trend') {
      let tooltipHtml = '',
        s = [];
      _(ev.points)
        .orderBy(p => p.series.name, 'desc')
        .forEach(point => {
          tooltipHtml += `<div class="tooltip_label_item" style="margin: 4px 5px 1px 0; line-height: 1.4vh;"><div style="position: relative; top: 0px; display: inline-block; width: 8px; height: 8px; margin-left: 4px; margin-right: 8px; transform: rotate(45deg); background: ${
            point.color
          }"></div>${point.series.name}%: ${point.point.y} day${point.point.y != 1 ? 's' : ''}</div>`;
        });
      s.push(tooltipHtml);
      return s;
    }

    const unit = 'month'; //isoWeek, month;
    const date = `${moment(ev.x)
      .startOf(unit)
      .format(this.props.filters.dateFormat)} - ${moment(ev.x)
      .endOf(unit)
      .format(this.props.filters.dateFormat)}`;

    let tooltip = `<p style="font-size: 13px; margin: 10px 10px 0;">Date: ${this.props.showExactDate ? moment(ev.x).format(this.props.filters.dateFormat): date}</p>`;
    if (!comp.props.detailed) {
      const value = `${ev.y} ${comp.props.unit}${comp.props.plural && ev.y != 1 ? 's' : ''}`;
      tooltip += `<p style="margin: 8px 10px 0; font-size: 13px;">${
        !!comp.props.tooltipLabel ? `${comp.props.tooltipLabel}: ` : ''
      }${value}</p>`;
    } else {
      _.forEach(ev.points, point => {
        const v = `${point.point.y} ${comp.props.unit}${comp.props.plural && ev.y != 1 ? 's' : ''}`;
        tooltip += `<div className="tooltip_label_item" style="margin: 4px 5px 1px 0; line-height: 20px;"><div style="position: relative; top: 0px; display: inline-block; width: 8px; height: 8px; margin-left: 4px; margin-right: 8px; transform: rotate(45deg); background: ${point.color}"></div>${point.series.name}: ${v}</div>`;
      });
    }

    return tooltip;
  }

  render() {
    return (
      <div id={this.props.id}>
        <div className="chart_tooltip_style">
          <div id={`${this.props.id}-details`}></div>
        </div>
      </div>
    );
  }
}

const mapStateToProps = state => {
  return {
    filters: state.report.filters
  };
};

export default connect(mapStateToProps)(LineChart);
